import { routeFileSelector } from '@/utilz';
import EmptyRecords from '@/views/components/Common/EmptyRecords';
import ScrollPagination from '@/views/components/Paginations/ScrollPagination';
import { Col, Divider, Row } from 'antd';
import { cloneDeep, groupBy, maxBy } from 'es-toolkit';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  getLADClaimFilesInProgress,
  updateLossAndDamageClaimDetailsInProgress,
} from '../../../../../../redux/Claims/LossAndDamage/LossAndDamageClaimsActions';
import { LADCovertDatesFromMomentToAPIType } from '../../../../../../utilz/PreClaimsUtils';
import Button from '../../../../../components/Buttons/Button';
import PrimaryLabel from '../../../../../components/Labels/PrimaryLabel';
import LADClaimsFilesCard from './LADClaimsFilesCard';

const LADClaimsFilesTab = () => {
  const dispatch = useDispatch();
  const { claimDetails } = useSelector((state) => state.lossAndDamage);
  const [filesList, setFilesList] = useState([]);
  const claimFiles = useSelector((state) => state.lossAndDamage.claimFiles);
  const [pageIndex, setPageIndex] = useState(1);
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  useEffect(() => {
    if (claimFiles.success && claimFiles?.data) {
      setFilesList(convertFromAPIToLocal(claimFiles.data.map((data) => ({ ...data, isNew: false }))));
    }
  }, [claimFiles]);

  const convertFromAPIToLocal = (files) => {
    // TODO: use native Object.groupBy
    const filesL = groupBy(files, (item) => item.documentType);
    const arrayFiles = [];
    for (const key in filesL) {
      arrayFiles.push({ ...filesL[key][0], fileList: filesL[key] });
    }
    return arrayFiles;
  };

  useEffect(() => {
    if (claimDetails.data?.claimKey) {
      dispatch(
        getLADClaimFilesInProgress({
          claimKey: claimDetails.data?.claimKey,
          pageIndex,
        }),
      );
    }
  }, [claimDetails.data?.claimKey]);

  const handleNextPage = () => {
    if (claimDetails.data?.claimKey) {
      const nextPageNo = pageIndex + 1;
      dispatch(
        getLADClaimFilesInProgress({
          claimKey: claimDetails.data?.claimKey,
          pageIndex: nextPageNo,
        }),
      );
      setPageIndex(nextPageNo);
    }
  };

  const setEditMode = (index, val) => {
    const filesListLocal = cloneDeep(filesList);
    filesListLocal[index].editMode = val;
    setFilesList(filesListLocal);
  };

  const handleCancel = (index, isNew) => () => {
    const filesLocal = cloneDeep(filesList);
    if (isNew) {
      filesLocal.splice(index, 1);
    } else {
      filesLocal[index] = {
        ...filesLocal[index],
        editMode: false,
      };
    }
    setFilesList(filesLocal);
  };

  const handleSubmit = (data) => {
    let payload = cloneDeep(claimDetails?.data);
    let filesListL = cloneDeep(filesList);
    filesListL = [data];
    payload = LADCovertDatesFromMomentToAPIType(payload);
    payload.claimFile = convertLocalToAPI(filesListL);
    payload.section = 'FILES';
    payload.pageIndex = pageIndex;
    dispatch(updateLossAndDamageClaimDetailsInProgress(payload));
  };

  const convertLocalToAPI = (files) => {
    const arrayFiles = [];
    files.forEach(({ fileList, dirtyFlag, isNew, editMode, ...data }) => {
      fileList.forEach(({ sequence, file, isNew, editMode, ...rest }) => {
        arrayFiles.push({ ...rest, ...data, sequence, fileContent: file });
      });
    });
    return arrayFiles;
  };

  const handleDelete = (index) => () => {
    let payload = cloneDeep(claimDetails?.data);
    const fileDetails = cloneDeep(filesList[index]);
    fileDetails.fileList = fileDetails.fileList.map((row) => ({
      ...row,
      dirtyFlag: 'D',
    }));
    payload = LADCovertDatesFromMomentToAPIType(payload);
    payload.claimFile = convertLocalToAPI([fileDetails]);
    payload.section = 'FILES';
    payload.pageIndex = pageIndex;
    dispatch(updateLossAndDamageClaimDetailsInProgress(payload));
  };

  const handleAddNew = () => () => {
    let filesListLocal = cloneDeep(filesList);
    let nextSequence = maxBy(filesListLocal, ({ sequence }) => Number(sequence));
    nextSequence = nextSequence ? nextSequence.sequence : 0;
    filesListLocal = [
      {
        claimKey: claimDetails?.data?.claimKey,
        sequence: nextSequence + 1,
        fileDesc: undefined,
        fileComments: undefined,
        fileName: undefined,
        originalFileName: undefined,
        documentType: undefined,
        description: undefined,
        isNew: true,
        editMode: true,
      },
      ...filesListLocal,
    ];
    setFilesList(filesListLocal);
  };
  return (
    <div className="flex-direction-column d-flex h-100 overflow-hidden">
      <Row className="align-items-center pt-15 px-30" justify="space-between">
        <Col>
          <PrimaryLabel className="fw-700 f-18">Upload/View Files</PrimaryLabel>
        </Col>
        <Col>
          <Row justify="end" gutter={16}>
            <Col>
              <Button onClick={handleAddNew()}>Add New Document</Button>
            </Col>
          </Row>
        </Col>
      </Row>
      <Divider className="mt-0 my-16 ml-n16 vw-100 " />
      <div className="flex-1 custom-scroll-bar">
        {filesList.length > 0 || claimFiles.progressing ? (
          <ScrollPagination
            className="px-30 py-10 flex-1"
            loadMore={handleNextPage}
            isLoading={claimFiles.progressing}
            hasMore={filesList.length < claimFiles.totalRows}
            scrollOffset={30}
          >
            {filesList.map((row, index) => (
              <LADClaimsFilesCard
                fileDetails={row}
                key={row.sequence}
                index={index}
                editMode={Boolean(row.editMode)}
                setEditMode={setEditMode}
                handleCancel={handleCancel}
                handleSubmit={handleSubmit}
                handleDelete={handleDelete}
                fileParamName="file"
              />
            ))}
          </ScrollPagination>
        ) : (
          <EmptyRecords
            title={t('translation:default.documentManager.noFoundText')}
            descriptionOne={t('translation:claims.emptyViewFiles')}
            descriptionTwo={''}
          />
        )}
      </div>
    </div>
  );
};

export default LADClaimsFilesTab;
